import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ProductType } from '@/types/ai/base';

export interface HomeApi {
    getProducts(): Promise<GetProductsResponse[]>;
    changeHomePage(home_page_type: HomePageType): Promise<boolean>;
    getHomePageType(): Promise<{homepage_type: HomePageType}>;
    getInitInfo(): Promise<GetInitResponse>;
    getExamples(): Promise<InputExample[]>;
    getAgents(data: GetAgentsRequest): Promise<GetAgentsResponse>;
    getRecommends(): Promise<GetAgentsResponse>;
}

export interface CardItem {
    // 功能ID
    function_id: string;
    // 产品类型
    product_type: ProductType;
    // 卡片名称
    name: string;
    // 目标
    goal: string;
    // 图片地址
    icon_url: string;
    type: 'A' | 'P';
    agent_id: string;
    team_id: string;
    description: string;
    is_free: boolean;
    // 是否第三方
    is_third_party?: boolean;
}

export interface GetAgentsRequest {
    page?: number;
    size?: number;
    productType?: string;
}

export interface GetAgentsResponse {
    cards: CardItem[];
}

export interface InputExample {
    function_id?: string;
    id?: string;
    desc: string;
    link: string;
    product_type: ProductType;
    type: 'A' | 'P';
    name?: string;
    key?: string
}

export type HomePageType = 'RD' | 'LS' | 'MSE' | 'IP' | 'LS_PLG' | 'RD_PLG' | 'HOME' | 'RD-HOME' | 'TELECOM';
export interface GetProductsResponse {
    code?: HomePageType;
    name?: string;
    description?: string;
}
export interface GetInitResponse {
    agent_model?: 'planning' | 'seek_agent',
    custom_table_permission: boolean;
    is_create_agent: boolean;
    tab_labels: Array<{
        is_freemium: boolean;
        product_type: ProductType;
    }>;
}
export const HomeApi = Symbol('HomeApi');

@injectable()
export class HomeApiImpl extends PsHttpService implements HomeApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    getInitInfo() {
        return this.get<PatSnapCommonResponseData<GetInitResponse>>({
            url: '/eureka/homepage/init',
            method: 'GET',
        }).then((res) => {
            return res?.data as unknown as GetInitResponse;
        });
    }

    getExamples() {
        return this.get<PatSnapCommonResponseData<InputExample[]>>({
            url: '/eureka/agent/example',
            method: 'GET',
        }).then((res) => {
            return res?.data as unknown as InputExample[];
        });
    }

    getAgents(data: GetAgentsRequest) {
        return this.get<PatSnapCommonResponseData<GetAgentsResponse>>({
            url: '/eureka/product-cards',
            method: 'GET',
            params: data,
        }).then((res) => {
            return res?.data as unknown as GetAgentsResponse;
        });
    }

    getRecommends() {
        return this.get<PatSnapCommonResponseData<GetAgentsResponse>>({
            url: '/eureka/recommend-cards',
            method: 'GET',
        }).then((res) => {
            return res?.data as unknown as GetAgentsResponse;
        });
    }

    getProducts() {
        return this.get<PatSnapCommonResponseData<GetProductsResponse[]>>({
            url: '/eureka/homepage/options',
            method: 'GET',
        }).then((res) => {
            return res?.data as unknown as GetProductsResponse[];
        }).catch((err) => {
            console.error('getRdProducts error', err);
            return [] as unknown as GetProductsResponse[];
        });
    }

    getHomePageType() {
        return this.get<PatSnapCommonResponseData<{homepage_type: HomePageType}>>({
            url: '/eureka/rd-home-page/redirect',
            method: 'GET',
        }).then((res) => {
            return res?.data as unknown as {homepage_type: HomePageType};
        }).catch((err) => {
            console.error('getHomePageType error', err);
            return { homepage_type: 'RD' } as unknown as {homepage_type: HomePageType};
        });
    }

    changeHomePage(home_page_type: HomePageType) {
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: '/eureka/rd-home-page/switch',
            method: 'POST',
            data: {
                home_page_type,
            },
        }).then((res) => {
            return res?.data as unknown as boolean;
        }).catch((err) => {
            console.error('changeHomePage error', err);
            return false;
        });
    }
}
